import {
  type CreateQuestionSectionRequest,
  type Paginated,
  type QuestionSection,
  type QuestionSectionDetail,
  type QuestionSectionsListQuerySchema,
  type ReorderItemsRequest,
  type UpdateQuestionSectionRequest,
} from '@aysar/contracts';
import { createApi } from '@reduxjs/toolkit/query/react';
import type { z } from 'zod';

import { EVENT_BUS } from '@/lib/event-bus';
import { baseQueryWithAuth } from '@/shared/store/middleware/base-query-with-auth/base-query-with-auth';

type SectionsParams = z.input<typeof QuestionSectionsListQuerySchema>;

/**
 * Registration and profile-wizard **sections** — the question engine's top level.
 *
 * Reordering sends the **full** ordered id list, because that is the api's shape:
 * it derives `sortOrder` from array position and refuses a partial list
 * (`question_reorder_incomplete`). A patch of "this one moved to 3" would leave the
 * server guessing what happened to the rest.
 */
export const questionSectionsApi = createApi({
  reducerPath: 'questionSectionsApi',
  baseQuery: baseQueryWithAuth,
  tagTypes: ['Section', 'SectionList'],
  endpoints: (builder) => ({
    listSections: builder.query<Paginated<QuestionSection>, SectionsParams>({
      query: (params) => ({ url: '/admin/questions/sections', method: 'GET', params }),
      providesTags: ['SectionList'],
    }),

    sectionDetail: builder.query<QuestionSectionDetail, string>({
      query: (id) => ({ url: `/admin/questions/sections/${id}`, method: 'GET' }),
      providesTags: (_result, _error, id) => [{ type: 'Section' as const, id }],
    }),

    createSection: builder.mutation<QuestionSectionDetail, CreateQuestionSectionRequest>({
      query: (body) => ({ url: '/admin/questions/sections', method: 'POST', body }),
      invalidatesTags: ['SectionList'],
      async onQueryStarted(_arg, { queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          EVENT_BUS.publish({ type: 'questions.section.changed', sectionId: data.id });
        } catch {
          // Refused — nothing to announce. Swallowed deliberately: `onQueryStarted`
          // has no caller, so a rejection escaping it becomes an unhandled
          // rejection. The failure is already the caller's, through `unwrap()`.
        }
      },
    }),

    updateSection: builder.mutation<
      QuestionSectionDetail,
      { id: string; body: UpdateQuestionSectionRequest }
    >({
      query: ({ id, body }) => ({
        url: `/admin/questions/sections/${id}`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: (_r, _e, { id }) => ['SectionList', { type: 'Section' as const, id }],
      async onQueryStarted({ id }, { queryFulfilled }) {
        try {
          await queryFulfilled;
          EVENT_BUS.publish({ type: 'questions.section.changed', sectionId: id });
        } catch {
          // See `createSection`.
        }
      },
    }),

    deleteSection: builder.mutation<void, string>({
      query: (id) => ({ url: `/admin/questions/sections/${id}`, method: 'DELETE' }),
      invalidatesTags: ['SectionList'],
      async onQueryStarted(id, { queryFulfilled }) {
        try {
          await queryFulfilled;
          EVENT_BUS.publish({ type: 'questions.section.changed', sectionId: id });
        } catch {
          // See `createSection`.
        }
      },
    }),

    reorderSections: builder.mutation<
      QuestionSection[],
      { flow: string; body: ReorderItemsRequest }
    >({
      query: ({ flow, body }) => ({
        url: `/admin/questions/sections/reorder/${flow}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: ['SectionList'],
      async onQueryStarted(_arg, { queryFulfilled }) {
        try {
          await queryFulfilled;
          EVENT_BUS.publish({ type: 'questions.section.changed', sectionId: null });
        } catch {
          // See `createSection`.
        }
      },
    }),
  }),
});

export const {
  useListSectionsQuery,
  useSectionDetailQuery,
  useCreateSectionMutation,
  useUpdateSectionMutation,
  useDeleteSectionMutation,
  useReorderSectionsMutation,
} = questionSectionsApi;
